'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { ShieldCheckIcon, DevicePhoneMobileIcon, ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Suspense } from 'react';

const ROLE_DASHBOARDS: Record<string, string> = {
  b2c_individual: '/b2c-dashboard',
  b2b_fleet: '/b2b-dashboard',
  operator: '/operator-dashboard',
  admin: '/admin-ops-portal',
};

function PremiumBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base: deep charcoal-black with warm undertone */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(160deg, #0b0c10 0%, #111318 30%, #0d1117 60%, #0b0c10 100%)',
      }} />

      {/* Large amber/gold radial — top left, like a distant city glow */}
      <div className="absolute" style={{
        top: '-10%', left: '-5%',
        width: '70vw', height: '70vw',
        background: 'radial-gradient(ellipse, rgba(251,146,60,0.07) 0%, rgba(234,88,12,0.04) 40%, transparent 70%)',
        borderRadius: '50%',
      }} />

      {/* Deep blue radial — bottom right */}
      <div className="absolute" style={{
        bottom: '-15%', right: '-10%',
        width: '65vw', height: '65vw',
        background: 'radial-gradient(ellipse, rgba(30,64,175,0.12) 0%, rgba(29,78,216,0.06) 40%, transparent 70%)',
        borderRadius: '50%',
      }} />

      {/* Subtle teal accent — center */}
      <div className="absolute" style={{
        top: '30%', left: '40%',
        width: '40vw', height: '40vw',
        background: 'radial-gradient(ellipse, rgba(6,182,212,0.04) 0%, transparent 60%)',
        borderRadius: '50%',
        transform: 'translate(-50%, -50%)',
      }} />

      {/* Perspective highway — full bleed, photorealistic feel */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        style={{ opacity: 1 }}
      >
        <defs>
          {/* Road surface gradient */}
          <linearGradient id="roadSurface" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(15,20,30,0)" />
            <stop offset="40%" stopColor="rgba(20,25,35,0.6)" />
            <stop offset="100%" stopColor="rgba(25,30,40,0.95)" />
          </linearGradient>
          {/* Lane marking glow */}
          <linearGradient id="laneGlow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(251,191,36,0)" />
            <stop offset="30%" stopColor="rgba(251,191,36,0.5)" />
            <stop offset="100%" stopColor="rgba(251,191,36,0.8)" />
          </linearGradient>
          {/* White lane lines */}
          <linearGradient id="whiteLane" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
            <stop offset="30%" stopColor="rgba(255,255,255,0.3)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0.6)" />
          </linearGradient>
          {/* Headlight streak */}
          <linearGradient id="headlight" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(255,255,255,0)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.9)" />
            <stop offset="100%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
          {/* Taillight streak */}
          <linearGradient id="taillight" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgba(239,68,68,0)" />
            <stop offset="50%" stopColor="rgba(239,68,68,0.8)" />
            <stop offset="100%" stopColor="rgba(239,68,68,0)" />
          </linearGradient>
          {/* Horizon glow */}
          <radialGradient id="horizonGlow" cx="50%" cy="0%" r="50%">
            <stop offset="0%" stopColor="rgba(251,146,60,0.15)" />
            <stop offset="60%" stopColor="rgba(234,88,12,0.05)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          {/* City glow on horizon */}
          <radialGradient id="cityGlow" cx="50%" cy="100%" r="60%">
            <stop offset="0%" stopColor="rgba(251,146,60,0.2)" />
            <stop offset="50%" stopColor="rgba(251,146,60,0.06)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <filter id="blur4">
            <feGaussianBlur stdDeviation="4" />
          </filter>
          <filter id="blur2">
            <feGaussianBlur stdDeviation="2" />
          </filter>
          <filter id="blur8">
            <feGaussianBlur stdDeviation="8" />
          </filter>
        </defs>

        {/* Sky / horizon glow */}
        <rect x="0" y="0" width="1440" height="500" fill="url(#horizonGlow)" />

        {/* Road surface */}
        <polygon points="720,320 0,900 1440,900" fill="url(#roadSurface)" />

        {/* Road edges — outer kerb lines */}
        <line x1="720" y1="320" x2="0" y2="900" stroke="rgba(255,255,255,0.12)" strokeWidth="2" />
        <line x1="720" y1="320" x2="1440" y2="900" stroke="rgba(255,255,255,0.12)" strokeWidth="2" />

        {/* Lane dividers — left of center */}
        <line x1="720" y1="320" x2="280" y2="900" stroke="url(#whiteLane)" strokeWidth="1.5" strokeDasharray="28,22" />
        <line x1="720" y1="320" x2="480" y2="900" stroke="url(#whiteLane)" strokeWidth="1.5" strokeDasharray="28,22" />

        {/* Lane dividers — right of center */}
        <line x1="720" y1="320" x2="960" y2="900" stroke="url(#whiteLane)" strokeWidth="1.5" strokeDasharray="28,22" />
        <line x1="720" y1="320" x2="1160" y2="900" stroke="url(#whiteLane)" strokeWidth="1.5" strokeDasharray="28,22" />

        {/* Center double yellow line */}
        <line x1="720" y1="320" x2="700" y2="900" stroke="url(#laneGlow)" strokeWidth="2.5" />
        <line x1="720" y1="320" x2="740" y2="900" stroke="url(#laneGlow)" strokeWidth="2.5" />

        {/* Vanishing point glow */}
        <circle cx="720" cy="320" r="60" fill="rgba(251,146,60,0.08)" filter="url(#blur8)" />
        <circle cx="720" cy="320" r="20" fill="rgba(251,191,36,0.12)" filter="url(#blur4)" />

        {/* City skyline silhouette */}
        <g opacity="0.18" fill="rgba(30,40,60,1)">
          <rect x="0" y="240" width="60" height="80" />
          <rect x="55" y="200" width="40" height="120" />
          <rect x="90" y="220" width="50" height="100" />
          <rect x="135" y="180" width="35" height="140" />
          <rect x="165" y="210" width="45" height="110" />
          <rect x="205" y="230" width="30" height="90" />
          <rect x="230" y="195" width="55" height="125" />
          <rect x="280" y="215" width="40" height="105" />
          <rect x="315" y="240" width="25" height="80" />
          {/* Right side */}
          <rect x="1100" y="240" width="60" height="80" />
          <rect x="1155" y="200" width="40" height="120" />
          <rect x="1190" y="220" width="50" height="100" />
          <rect x="1235" y="180" width="35" height="140" />
          <rect x="1265" y="210" width="45" height="110" />
          <rect x="1305" y="230" width="30" height="90" />
          <rect x="1330" y="195" width="55" height="125" />
          <rect x="1380" y="215" width="40" height="105" />
          <rect x="1415" y="240" width="25" height="80" />
        </g>

        {/* City glow behind skyline */}
        <ellipse cx="200" cy="320" rx="250" ry="80" fill="rgba(251,146,60,0.06)" filter="url(#blur8)" />
        <ellipse cx="1240" cy="320" rx="250" ry="80" fill="rgba(251,146,60,0.06)" filter="url(#blur8)" />

        {/* Headlight streaks — cars coming toward viewer */}
        <rect x="580" y="440" width="80" height="1.5" fill="url(#headlight)" filter="url(#blur2)" opacity="0.7" />
        <rect x="580" y="443" width="80" height="1" fill="url(#headlight)" opacity="0.4" />
        <rect x="760" y="440" width="80" height="1.5" fill="url(#headlight)" filter="url(#blur2)" opacity="0.7" />
        <rect x="760" y="443" width="80" height="1" fill="url(#headlight)" opacity="0.4" />

        {/* Taillight streaks — cars going away */}
        <rect x="340" y="560" width="120" height="2" fill="url(#taillight)" filter="url(#blur2)" opacity="0.6" />
        <rect x="980" y="560" width="120" height="2" fill="url(#taillight)" filter="url(#blur2)" opacity="0.6" />

        {/* Street lamp posts — left side */}
        <line x1="200" y1="320" x2="180" y2="900" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
        <circle cx="200" cy="320" r="8" fill="rgba(251,191,36,0.5)" filter="url(#blur4)" />
        <line x1="380" y1="370" x2="350" y2="900" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" />
        <circle cx="380" cy="370" r="6" fill="rgba(251,191,36,0.4)" filter="url(#blur4)" />
        <line x1="510" y1="410" x2="490" y2="900" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        <circle cx="510" cy="410" r="4" fill="rgba(251,191,36,0.3)" filter="url(#blur4)" />

        {/* Street lamp posts — right side */}
        <line x1="1240" y1="320" x2="1260" y2="900" stroke="rgba(255,255,255,0.08)" strokeWidth="2" />
        <circle cx="1240" cy="320" r="8" fill="rgba(251,191,36,0.5)" filter="url(#blur4)" />
        <line x1="1060" y1="370" x2="1090" y2="900" stroke="rgba(255,255,255,0.06)" strokeWidth="1.5" />
        <circle cx="1060" cy="370" r="6" fill="rgba(251,191,36,0.4)" filter="url(#blur4)" />
        <line x1="930" y1="410" x2="950" y2="900" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        <circle cx="930" cy="410" r="4" fill="rgba(251,191,36,0.3)" filter="url(#blur4)" />

        {/* Wet road reflection — subtle shimmer */}
        <ellipse cx="720" cy="700" rx="300" ry="40" fill="rgba(30,64,175,0.08)" filter="url(#blur8)" />
        <ellipse cx="720" cy="750" rx="200" ry="25" fill="rgba(251,146,60,0.05)" filter="url(#blur8)" />

        {/* Top: subtle star field */}
        {[
          [120,40],[300,20],[450,60],[600,15],[800,35],[950,55],[1100,25],[1250,45],[1380,30],
          [180,80],[380,90],[550,70],[750,85],[900,75],[1050,95],[1200,65],[80,100],[1350,85],
        ].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 2 === 0 ? 1 : 0.7} fill="rgba(255,255,255,0.4)" />
        ))}
      </svg>

      {/* Grain texture overlay for premium feel */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
        backgroundSize: '128px 128px',
        mixBlendMode: 'overlay',
      }} />

      {/* Vignette */}
      <div className="absolute inset-0" style={{
        background: 'radial-gradient(ellipse 80% 80% at 50% 50%, transparent 40%, rgba(0,0,0,0.6) 100%)',
      }} />

      {/* Traffic signal — top right */}
      <div className="absolute top-8 right-12 hidden xl:flex flex-col items-center gap-0.5 opacity-30">
        <div className="w-9 h-24 rounded-lg flex flex-col items-center justify-around py-2" style={{
          background: 'linear-gradient(180deg, #1a1f2e 0%, #0d1117 100%)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 0 15px rgba(0,0,0,0.5)',
        }}>
          <div className="w-4 h-4 rounded-full" style={{ background: '#ef4444', boxShadow: '0 0 10px #ef4444, 0 0 20px rgba(239,68,68,0.4)' }} />
          <div className="w-4 h-4 rounded-full" style={{ background: '#1a1f2e', border: '1px solid rgba(251,191,36,0.3)' }} />
          <div className="w-4 h-4 rounded-full" style={{ background: '#1a1f2e', border: '1px solid rgba(34,197,94,0.3)' }} />
        </div>
        <div className="w-1 h-12 rounded-full" style={{ background: 'rgba(255,255,255,0.15)' }} />
        <div className="w-6 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.1)' }} />
      </div>

      {/* CSS animations */}
      <style>{`
        @keyframes shimmer {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}

function LoginForm() {
  const searchParams = useSearchParams();
  const nextUrl = searchParams?.get('next') || null;
  const reason = searchParams?.get('reason') || null;

  const [step, setStep] = useState<'mobile' | 'otp'>('mobile');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(reason === 'session_expired' ? 'Your session expired. Please sign in again.' : '');
  const [resendTimer, setResendTimer] = useState(0);
  const [requestId, setRequestId] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Sanitize raw provider error codes into user-friendly messages
  const friendlyOtpError = (raw: string): string => {
    const lower = (raw || '').toLowerCase();
    if (lower.includes('usage_exceeded') || lower.includes('usage exceeded') || lower.includes('quota') || lower.includes('rate limit')) {
      return 'OTP service is temporarily unavailable. Please try again in a few minutes.';
    }
    if (lower.includes('invalid') && lower.includes('phone')) {
      return 'Invalid mobile number. Please check and try again.';
    }
    if (lower.includes('network') || lower.includes('timeout') || lower.includes('connection')) {
      return 'Network error. Please check your connection and try again.';
    }
    // If it looks like a raw code (no spaces, underscores), replace it
    if (/^[a-z_]+$/.test(lower) && !lower.includes(' ')) {
      return 'Failed to send OTP. Please try again.';
    }
    return raw || 'Failed to send OTP. Please try again.';
  };

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const startResendTimer = () => {
    setResendTimer(30);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) { if (timerRef.current) clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanMobile = mobile.replace(/\s/g, '');
    if (cleanMobile.length !== 10 || !/^\d{10}$/.test(cleanMobile)) {
      setError('Please enter a valid 10-digit mobile number.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: cleanMobile }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(friendlyOtpError(data.error || 'Failed to send OTP. Please try again.'));
        return;
      }
      setRequestId(data.request_id || null);
      setStep('otp');
      startResendTimer();
    } catch {
      setError('Failed to send OTP. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanOtp = otpDigits.join('');
    if (cleanOtp.length !== 6 || !/^\d{6}$/.test(cleanOtp)) {
      setError('Please enter the 6-digit OTP.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const cleanMobile = mobile.replace(/\s/g, '');
      const res = await fetch('/api/auth/verify-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: cleanMobile, otp: cleanOtp, request_id: requestId }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Invalid OTP. Please try again.');
        setLoading(false);
        return;
      }

      if (data.apikey) {
        sessionStorage.setItem('tc_apikey', data.apikey);
        sessionStorage.setItem('tc_orgid', data.orgId || '');
      }

      // Store role for demo fallback
      if (data.demo) {
        sessionStorage.setItem('demo_role', data.role || '');
        sessionStorage.setItem('demo_name', data.name || '');
      } else {
        // Real account: clear any stale demo session storage to prevent crosswire
        sessionStorage.removeItem('demo_role');
        sessionStorage.removeItem('demo_name');
        // Also clear demo cookies client-side (belt-and-suspenders — server already cleared them)
        document.cookie = 'demo_role=; path=/; max-age=0; SameSite=Lax';
        document.cookie = 'demo_mobile=; path=/; max-age=0; SameSite=Lax';
      }

      // Store the JWT token in localStorage — no cookies, no middleware race condition.
      // The dashboard pages and AuthContext read directly from localStorage.
      if (data.token) {
        try { localStorage.setItem('cd_token', data.token); } catch { /* ignore */ }
      }

      // Navigate directly to the dashboard — no commit-session redirect needed
      const roleDashboard = data.dashboard || ROLE_DASHBOARDS[data.role] || '/b2c-dashboard';
      const destination = data.demo ? roleDashboard : (nextUrl || roleDashboard);
      window.location.href = destination;
      // Keep button in "Verifying…" state during navigation
    } catch {
      setError('Verification failed. Please try again.');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setOtp('');
    setOtpDigits(['', '', '', '', '', '']);
    setError('');
    setLoading(true);
    try {
      const cleanMobile = mobile.replace(/\s/g, '');
      const res = await fetch('/api/auth/send-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: cleanMobile }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(friendlyOtpError(data.error || 'Failed to resend OTP.'));
        return;
      }
      setRequestId(data.request_id || null);
      startResendTimer();
    } catch {
      setError('Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpDigitChange = (index: number, value: string) => {
    const digit = value.replace(/[^0-9]/g, '').slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);
    setOtp(newDigits.join(''));
    // Move focus to next box if digit entered
    if (digit && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (otpDigits[index]) {
        // Clear current box
        const newDigits = [...otpDigits];
        newDigits[index] = '';
        setOtpDigits(newDigits);
        setOtp(newDigits.join(''));
      } else if (index > 0) {
        // Move to previous box
        otpRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newDigits = [...otpDigits];
      for (let i = 0; i < 6; i++) {
        newDigits[i] = pasted[i] || '';
      }
      setOtpDigits(newDigits);
      setOtp(newDigits.join(''));
      // Focus the next empty box or last box
      const nextEmpty = newDigits.findIndex(d => !d);
      const focusIdx = nextEmpty === -1 ? 5 : nextEmpty;
      otpRefs.current[focusIdx]?.focus();
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12 overflow-hidden">
      <PremiumBackground />

      {/* Left side branding — desktop only */}
      <div className="absolute left-0 top-0 bottom-0 w-1/2 hidden lg:flex flex-col justify-between p-12 z-10 pointer-events-none">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
              background: 'linear-gradient(135deg, rgba(251,146,60,0.2) 0%, rgba(234,88,12,0.1) 100%)',
              border: '1px solid rgba(251,146,60,0.3)',
            }}>
              <AppLogo size={24} />
            </div>
            <span className="text-white font-bold text-lg tracking-tight">ChallanDesk</span>
          </div>
        </div>
        <div className="space-y-6 max-w-sm">
          <div className="space-y-3">
            <p className="text-xs font-semibold tracking-widest uppercase" style={{ color: 'rgba(251,146,60,0.8)' }}>
              India's Traffic Challan Platform
            </p>
            <h2 className="text-4xl font-bold text-white leading-tight" style={{ fontFamily: 'system-ui, sans-serif' }}>
              Settle challans.<br />
              <span style={{ color: 'rgba(251,146,60,0.9)' }}>At scale.</span>
            </h2>
            <p className="text-sm leading-relaxed" style={{ color: 'rgba(203,213,225,0.6)' }}>
              Trusted by fleet operators and businesses across India to manage, track, and settle traffic challans — all from one dashboard.
            </p>
          </div>
          <div className="flex items-center gap-6">
            {[
              { value: '2M+', label: 'Challans settled' },
              { value: '500+', label: 'Fleet clients' },
              { value: '99.9%', label: 'Uptime' },
            ].map(stat => (
              <div key={stat.label}>
                <p className="text-xl font-bold text-white">{stat.value}</p>
                <p className="text-xs" style={{ color: 'rgba(148,163,184,0.6)' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs" style={{ color: 'rgba(100,116,139,0.6)' }}>
          Operated by Pichain Innovations Private Limited
        </p>
      </div>

      {/* Login card — right side on desktop, centered on mobile */}
      <div className="relative w-full max-w-sm z-10 lg:ml-auto lg:mr-16 xl:mr-24">
        {/* Mobile logo */}
        <div className="lg:hidden text-center mb-8 space-y-2">
          <div className="inline-flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{
              background: 'linear-gradient(135deg, rgba(251,146,60,0.2) 0%, rgba(234,88,12,0.1) 100%)',
              border: '1px solid rgba(251,146,60,0.3)',
            }}>
              <AppLogo size={24} />
            </div>
            <span className="text-white font-bold text-xl tracking-tight">ChallanDesk</span>
          </div>
          <p className="text-xs" style={{ color: 'rgba(148,163,184,0.6)' }}>
            Operated by Pichain Innovations Private Limited
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-8 space-y-6" style={{
          background: 'rgba(13,17,23,0.85)',
          backdropFilter: 'blur(32px)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: '0 32px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04), inset 0 1px 0 rgba(255,255,255,0.06)',
        }}>
          <div className="space-y-1">
            <h1 className="text-xl font-bold text-white">
              {step === 'mobile' ? 'Sign in to your account' : 'Enter verification code'}
            </h1>
            <p className="text-sm" style={{ color: 'rgba(148,163,184,0.7)' }}>
              {step === 'mobile' ?'Enter your registered mobile number'
                : `OTP sent to +91 ${mobile}`}
            </p>
          </div>

          {error && (
            <div className="text-sm px-4 py-3 rounded-xl flex items-start gap-2" style={{
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.25)',
              color: '#fca5a5',
            }}>
              <span className="flex-shrink-0 mt-0.5">⚠</span>
              <span>{error}</span>
            </div>
          )}

          {step === 'mobile' ? (
            <form onSubmit={handleSendOTP} className="space-y-5">
              <div className="space-y-2">
                <label className="block text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(148,163,184,0.7)' }}>
                  Mobile Number
                </label>
                <div className="flex items-center gap-2 w-full px-4 py-3.5 rounded-xl transition-all focus-within:ring-1" style={{
                  border: '1px solid rgba(255,255,255,0.1)',
                  background: 'rgba(255,255,255,0.04)',
                  '--tw-ring-color': 'rgba(251,146,60,0.4)',
                } as React.CSSProperties}>
                  <span className="font-bold text-sm flex-shrink-0" style={{ color: 'rgba(251,146,60,0.9)' }}>+91</span>
                  <div className="w-px h-4" style={{ background: 'rgba(255,255,255,0.12)' }} />
                  <DevicePhoneMobileIcon className="w-4 h-4 flex-shrink-0" style={{ color: 'rgba(100,116,139,0.7)' }} />
                  <input
                    type="tel"
                    required
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                    placeholder="10-digit mobile number"
                    className="flex-1 bg-transparent focus:outline-none text-sm font-mono tracking-wider placeholder:text-slate-600"
                    style={{ color: 'white' }}
                    maxLength={10}
                    autoComplete="tel"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || mobile.replace(/\s/g, '').length !== 10}
                className="w-full py-3.5 text-white rounded-xl text-sm font-semibold transition-all duration-200 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                  boxShadow: mobile.replace(/\s/g, '').length === 10 && !loading
                    ? '0 0 24px rgba(234,88,12,0.35), 0 4px 12px rgba(234,88,12,0.25)' :'none',
                }}
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <ArrowRightIcon className="w-4 h-4" />
                )}
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </button>

              <p className="text-center text-sm" style={{ color: 'rgba(100,116,139,0.8)' }}>
                New to ChallanDesk?{' '}
                <Link href="/register" className="font-semibold transition-colors" style={{ color: 'rgba(251,146,60,0.9)' }}>
                  Sign up free
                </Link>
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-5">
              <div className="space-y-2">
                <label className="block text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(148,163,184,0.7)' }}>
                  6-digit OTP
                </label>
                <div className="flex gap-2 justify-between" onPaste={handleOtpPaste}>
                  {otpDigits.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => { otpRefs.current[index] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpDigitChange(index, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(index, e)}
                      autoFocus={index === 0}
                      autoComplete={index === 0 ? 'one-time-code' : 'off'}
                      className="w-12 h-14 rounded-xl text-center text-xl font-bold font-mono focus:outline-none focus:ring-2 transition-all"
                      style={{
                        background: digit ? 'rgba(251,146,60,0.12)' : 'rgba(255,255,255,0.04)',
                        border: digit ? '1.5px solid rgba(251,146,60,0.6)' : '1px solid rgba(255,255,255,0.1)',
                        color: 'white',
                        caretColor: 'rgba(251,146,60,0.9)',
                      }}
                    />
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || otpDigits.join('').length !== 6}
                className="w-full py-3.5 text-white rounded-xl text-sm font-semibold transition-all duration-200 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
                  boxShadow: otpDigits.join('').length === 6 && !loading
                    ? '0 0 24px rgba(234,88,12,0.35), 0 4px 12px rgba(234,88,12,0.25)' :'none',
                }}
              >
                {loading ? (
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <ShieldCheckIcon className="w-4 h-4" />
                )}
                {loading ? 'Verifying...' : 'Verify & Sign In'}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => { setStep('mobile'); setOtp(''); setOtpDigits(['', '', '', '', '', '']); setError(''); setRequestId(null); }}
                  className="transition-colors flex items-center gap-1"
                  style={{ color: 'rgba(100,116,139,0.8)' }}
                >
                  <ArrowLeftIcon className="w-3 h-3" /> Change number
                </button>
                {resendTimer > 0 ? (
                  <span style={{ color: 'rgba(100,116,139,0.6)' }}>Resend in {resendTimer}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={loading}
                    className="font-semibold transition-colors disabled:opacity-50"
                    style={{ color: 'rgba(251,146,60,0.9)' }}
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </form>
          )}
        </div>

        <p className="text-center text-xs mt-6" style={{ color: 'rgba(71,85,105,0.7)' }}>
          <Link href="/" className="transition-colors hover:text-slate-400">← Back to ChallanDesk</Link>
        </p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0b0c10' }}>
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
}
